import {
  ApiError,
  ApiNotFoundError,
  ApiUnauthorizedError,
  type BlogPostDetail,
  type BlogPostSummary,
  type CatalogItemDetail,
  type CatalogItemSummary,
  type DetailEnvelope,
  type ListEnvelope,
  type LocaleCode,
  type MenuItem,
  type PageDetail,
  type PageListItem,
  type RedirectsEnvelope,
  type PortalUser,
  type PortalOrder,
  type PortalCustomsFile,
  type ReviewsListEnvelope,
  type ProductReview,
  type Address,
  type AddressInput,
  type AddressListEnvelope,
} from "./types";

/** Upper bound on any single Odoo request, in milliseconds. */
export const ODOO_REQUEST_TIMEOUT_MS = 15_000;

export interface ContentApiClientOptions {
  baseUrl: string;
  writeToken?: string; // Server-to-server authorization token
  sessionId?: string; // Authenticated user session cookie
  fetchImpl?: typeof fetch;
}

/**
 * Typed client to interface with the Odoo 19 REST API.
 * Handles both public read content endpoints, authenticated portal endpoints, and checkouts.
 */
export class ContentApiClient {
  constructor(private readonly options: ContentApiClientOptions) {}

  /**
   * Helper to make HTTP requests.
   * Injecting session ID or authorization header when provided.
   */
  private async requestJson<T>(
    path: string,
    method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
    body?: unknown,
  ): Promise<T> {
    const fetchImpl = this.options.fetchImpl ?? fetch;
    const url = `${this.options.baseUrl}${path}`;

    const headers: Record<string, string> = {
      Accept: "application/json",
    };

    // If server-to-server token is set, attach as Bearer authorization
    if (this.options.writeToken) {
      headers["Authorization"] = `Bearer ${this.options.writeToken}`;
    }

    // If Odoo session ID is set, pass in Cookie header (or custom header)
    if (this.options.sessionId) {
      headers["Cookie"] = `session_id=${this.options.sessionId}`;
      headers["X-Openerp-Session-Id"] = this.options.sessionId;
    }

    if (body) {
      headers["Content-Type"] = "application/json";
    }

    // Bounded (VARS-169): with no timeout a stalled Odoo held a lead
    // submission open until the Worker itself was killed, and the buyer saw a
    // spinner rather than an error they could act on (retry or WhatsApp).
    const response = await fetchImpl(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: AbortSignal.timeout(ODOO_REQUEST_TIMEOUT_MS),
    });

    if (response.status === 401) {
      throw new ApiUnauthorizedError(path);
    }
    if (response.status === 404) {
      // A 404 body is not required to be JSON, and a malformed one must not
      // turn a clean "not found" into a parse error.
      let canonicalPath: string | undefined;
      try {
        const body = (await response.json()) as { canonical_url_path?: unknown };
        if (typeof body?.canonical_url_path === "string") canonicalPath = body.canonical_url_path;
      } catch {
        // No usable body; the 404 stands on its own.
      }
      throw new ApiNotFoundError(path, canonicalPath);
    }
    if (!response.ok) {
      throw new ApiError(path, response.status);
    }

    return (await response.json()) as T;
  }

  // ==========================================
  // 1. PUBLIC READ CONTENT ENDPOINTS
  // ==========================================

  listPages(locale: LocaleCode): Promise<ListEnvelope<PageListItem>> {
    return this.requestJson(`/api/v1/pages/${locale}`);
  }

  getPage(locale: LocaleCode, urlPath: string): Promise<DetailEnvelope<PageDetail>> {
    const normalizedPath = urlPath === "/" ? "/" : urlPath.replace(/^\/+/, "");
    const encodedUrlPath = normalizedPath === "/" ? "_home" : this.encodeNestedPath(normalizedPath);
    return this.requestJson(`/api/v1/pages/${locale}/${encodedUrlPath}`);
  }

  listRedirects(): Promise<RedirectsEnvelope> {
    return this.requestJson("/api/v1/redirects");
  }

  getMenu(locale: LocaleCode): Promise<ListEnvelope<MenuItem>> {
    return this.requestJson(`/api/v1/menu/${locale}`);
  }

  listPosts(locale: LocaleCode): Promise<ListEnvelope<BlogPostSummary>> {
    return this.requestJson(`/api/v1/posts/${locale}`);
  }

  getPost(locale: LocaleCode, slug: string): Promise<DetailEnvelope<BlogPostDetail>> {
    return this.requestJson(`/api/v1/posts/${locale}/${encodeURIComponent(slug)}`);
  }

  listProducts(locale: LocaleCode): Promise<ListEnvelope<CatalogItemSummary>> {
    return this.requestJson(`/api/v1/products/${locale}`);
  }

  getProduct(locale: LocaleCode, urlPath: string): Promise<DetailEnvelope<CatalogItemDetail>> {
    const encodedUrlPath = this.encodeNestedPath(urlPath);
    return this.requestJson(`/api/v1/products/${locale}/${encodedUrlPath}`);
  }

  /**
   * Real Odoo storefront products (product.template, gated on is_published
   * via website_sale) — the data behind /shop. Distinct from listProducts()/
   * getProduct() above, which read the separate curated varsco.catalog.item
   * model behind the informational /products portfolio.
   */
  listStoreProducts(locale: LocaleCode): Promise<ListEnvelope<CatalogItemSummary>> {
    return this.requestJson(`/api/v1/store/products/${locale}`);
  }

  getStoreProduct(locale: LocaleCode, urlPath: string): Promise<DetailEnvelope<CatalogItemDetail>> {
    const encodedUrlPath = this.encodeNestedPath(urlPath);
    return this.requestJson(`/api/v1/store/products/${locale}/${encodedUrlPath}`);
  }

  listProductReviews(locale: LocaleCode, urlPath: string): Promise<ReviewsListEnvelope> {
    const encodedUrlPath = this.encodeNestedPath(urlPath);
    return this.requestJson(`/api/v1/store/products/${locale}/${encodedUrlPath}/reviews`);
  }

  /** Session-authenticated — the caller must set sessionId on this client instance. */
  submitProductReview(
    locale: LocaleCode,
    urlPath: string,
    review: { rating: number; feedback?: string },
  ): Promise<{ data: ProductReview }> {
    const encodedUrlPath = this.encodeNestedPath(urlPath);
    return this.requestJson(
      `/api/v1/store/products/${locale}/${encodedUrlPath}/reviews`,
      "POST",
      review,
    );
  }

  /** All three wishlist methods are session-authenticated — no guest wishlist. */
  listWishlist(): Promise<{ data: CatalogItemSummary[] }> {
    return this.requestJson("/api/v1/store/wishlist");
  }

  addToWishlist(productId: number): Promise<{ data: CatalogItemSummary }> {
    return this.requestJson("/api/v1/store/wishlist", "POST", { product_id: productId });
  }

  removeFromWishlist(productId: number): Promise<{ status: string }> {
    return this.requestJson(`/api/v1/store/wishlist/${productId}`, "DELETE");
  }

  /** All four address-book methods are session-authenticated. */
  listAddresses(): Promise<AddressListEnvelope> {
    return this.requestJson("/api/v1/store/addresses");
  }

  createAddress(address: AddressInput): Promise<{ data: Address }> {
    return this.requestJson("/api/v1/store/addresses", "POST", address);
  }

  updateAddress(id: number, address: Partial<AddressInput>): Promise<{ data: Address }> {
    return this.requestJson(`/api/v1/store/addresses/${id}`, "PUT", address);
  }

  deleteAddress(id: number): Promise<{ status: string }> {
    return this.requestJson(`/api/v1/store/addresses/${id}`, "DELETE");
  }

  // ==========================================
  // 2. LEAD SUBMISSIONS
  // ==========================================

  /**
   * Create a CRM lead from a form submission.
   *
   * Snake_case beyond the original five fields because that is the convention
   * on the Odoo side (varsco_content_api controllers/leads.py); the older
   * fields keep their existing names so this stays backward compatible with a
   * controller that has not been redeployed yet — unknown keys are ignored
   * there, so a newer frontend against an older addon degrades to the previous
   * behaviour rather than failing.
   */
  submitLead(payload: {
    name: string;
    email: string;
    company?: string;
    phone?: string;
    message: string;
    source: string;
    cart_summary?: string;
    country?: string;
    /** What the enquiry is about — drives the CRM lead subject. */
    topic?: string;
    product_slug?: string;
    product_title?: string;
    custom_fields?: Record<string, string>;
    page_path?: string;
    page_section?: string;
    locale?: string;
    /** Host only, not the full referring URL. */
    referrer_host?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    /** Keyword and creative — the level ad bids are actually decided at. */
    utm_term?: string;
    utm_content?: string;
    /**
     * The ad network's own click identifier (`gclid`/`wbraid`/`gbraid`, or
     * `fbclid`), and which network issued it. This is what Google Ads' and
     * Meta's offline conversion imports match on, so it is the field that makes
     * a closed-won lead attributable to the click that paid for it.
     *
     * Unknown keys are dropped by the Odoo controller, so a frontend that sends
     * these against an addon that does not yet read them degrades silently to
     * the previous behaviour rather than failing — see the note above and R1.
     */
    click_id?: string;
    click_id_source?: "google" | "meta";
    portal_user_id?: string;
    /**
     * 32 lowercase hex characters, same constraint as trackVisit — links the
     * lead to the website.visitor its pageviews were recorded under. Only
     * present when the buyer accepted analytics.
     */
    visitor_token?: string;
  }): Promise<{ status: string; lead_id: number }> {
    return this.requestJson("/api/v1/leads", "POST", payload);
  }

  /** Subscribe an address to the newsletter list (mailing.contact). */
  subscribeNewsletter(payload: {
    email: string;
    name?: string;
    company?: string;
  }): Promise<{ status: string; contact_id: number; already_subscribed: boolean }> {
    return this.requestJson("/api/v1/newsletter", "POST", payload);
  }

  /**
   * Forward a batch of pageviews for one visitor.
   *
   * `visitor_token` must be 32 lowercase hex characters — Odoo overloads
   * website.visitor.access_token, treating anything else as a res.partner id.
   */
  trackVisit(payload: {
    visitor_token: string;
    lang_code?: string;
    country_code?: string;
    events: { url: string; at?: string }[];
  }): Promise<{ status: string; visitor_id: number; tracked: number }> {
    return this.requestJson("/api/v1/track", "POST", payload);
  }

  // ==========================================
  // 3. USER PORTAL & AUTHENTICATION
  // ==========================================

  authenticateUser(credentials: {
    login: string;
    password: string;
  }): Promise<{ session_id: string; user: PortalUser }> {
    return this.requestJson("/api/v1/portal/auth/login", "POST", credentials);
  }

  registerUser(details: {
    name: string;
    email: string;
    phone: string;
    company: string;
    country: string;
    password: string;
  }): Promise<{ session_id: string; user: PortalUser }> {
    return this.requestJson("/api/v1/portal/auth/register", "POST", details);
  }

  requestPasswordReset(payload: { email: string }): Promise<{ status: string; message?: string }> {
    return this.requestJson("/api/v1/portal/auth/reset-password", "POST", payload);
  }

  changePassword(payload: {
    old_password: string;
    new_password: string;
  }): Promise<{ status: string; message?: string }> {
    return this.requestJson("/api/v1/portal/profile/password", "POST", payload);
  }

  getPortalOrders(): Promise<{ data: PortalOrder[] }> {
    return this.requestJson("/api/v1/portal/orders", "GET");
  }

  getPortalCustomsFiles(): Promise<{ data: PortalCustomsFile[] }> {
    return this.requestJson("/api/v1/portal/customs", "GET");
  }

  updatePortalProfile(profile: Partial<PortalUser>): Promise<{ status: string }> {
    return this.requestJson("/api/v1/portal/profile", "PUT", profile);
  }

  // ==========================================
  // 4. STORE CHECKOUTS
  // ==========================================

  /**
   * shipping/billing partner ids are optional overrides — when omitted, Odoo
   * resolves the ordering partner from the session cookie, same as the other
   * `auth="user"` portal endpoints (getPortalOrders, updatePortalProfile).
   */
  submitCheckout(checkoutData: {
    shipping_partner_id?: number;
    billing_partner_id?: number;
    items: { product_id: number; qty: number }[];
  }): Promise<{
    order_id: number;
    amount_total: number;
    currency: string;
    /** Absolute URL to Odoo's customer-portal "Pay Now" page — present only when a compatible payment.provider is configured. */
    payment_url?: string;
  }> {
    return this.requestJson("/api/v1/store/checkout", "POST", checkoutData);
  }

  // ==========================================
  // HELPERS
  // ==========================================

  private encodeNestedPath(urlPath: string): string {
    return urlPath.replace(/^\/+/, "").split("/").map(encodeURIComponent).join("/");
  }
}
